// A janela de 24h do WhatsApp.
//
// Regra da Meta: fora de 24h desde a última mensagem DO CLIENTE, só entra
// template aprovado. Texto livre é recusado — e, como o nosso WABA não tem
// nenhum template cadastrado, hoje isso significa simplesmente que a mensagem
// não sai.
//
// Isso não era visível em lugar nenhum. Um atendimento inteiro foi escrito 3h33
// depois do prazo: seis mensagens, todas recusadas, o painel mostrando tudo como
// enviado e o cliente sem receber nada. Daí este módulo — a conta é trivial, o
// que faltava era alguém fazê-la antes de o atendente digitar.

import type { ChatwootMessage } from '@/api/endpoints/support';

const JANELA_MS = 24 * 60 * 60 * 1000;

export type EstadoJanela =
  /** O cliente falou nas últimas 24h: pode escrever à vontade. */
  | { estado: 'aberta'; restanteMs: number; fechaEm: Date }
  /** Passou de 24h: mensagem livre não é entregue. */
  | { estado: 'fechada'; desdeMs: number }
  /** O cliente nunca escreveu (ou o histórico carregado não alcança). */
  | { estado: 'desconhecida' };

/** Última mensagem do cliente, em ms. `message_type === 0` é incoming. */
function ultimaDoCliente(messages: ChatwootMessage[]): number | null {
  let maior: number | null = null;
  for (const m of messages) {
    if (m.message_type !== 0 || !m.created_at) continue;
    // Reação a uma mensagem nossa não reabre a janela (a API também não conta).
    if (m.content?.startsWith('[Reagiu com ')) continue;
    const ts = m.created_at * 1000;
    if (maior === null || ts > maior) maior = ts;
  }
  return maior;
}

export function estadoDaJanela(messages: ChatwootMessage[], agora = Date.now()): EstadoJanela {
  const ultima = ultimaDoCliente(messages);
  if (ultima === null) return { estado: 'desconhecida' };

  const fecha = ultima + JANELA_MS;
  if (agora >= fecha) return { estado: 'fechada', desdeMs: agora - fecha };
  return { estado: 'aberta', restanteMs: fecha - agora, fechaEm: new Date(fecha) };
}

/** "6h12" · "48min" · "2 dias" — curto, para caber num chip. */
export function duracaoCurta(ms: number): string {
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${Math.max(min, 1)}min`;

  const horas = Math.floor(min / 60);
  if (horas < 24) {
    const resto = min % 60;
    return resto ? `${horas}h${String(resto).padStart(2, '0')}` : `${horas}h`;
  }

  const dias = Math.floor(horas / 24);
  return dias === 1 ? '1 dia' : `${dias} dias`;
}

/**
 * Menos de 2h restantes já merece destaque: é o tempo de o atendente ir almoçar
 * e voltar com o cliente inalcançável.
 */
export const JANELA_URGENTE_MS = 2 * 60 * 60 * 1000;
